/**
 * Les demandes d'inclusion, du côté du parent.
 *
 * Un enfant qui a des besoins particuliers ne s'inscrit pas d'un clic :
 * l'administration examine d'abord sa demande, pour l'accueillir en sécurité.
 * Avant, la page des stages remplaçait chaque semaine par un bouton « Faire une
 * demande d'inclusion », sans jamais dire qu'une demande était partie : les
 * parents recliquaient — 429 demandes pour 219 semaines en production, jusqu'à
 * dix fois la même (mesuré le 25/10/2026).
 *
 * Désormais, chaque semaine dit où en est sa demande, et le parent coche
 * plusieurs semaines pour UNE demande, une fois les inscriptions ouvertes.
 * L'équipe y répond : elle inscrit l'enfant et envoie la facture (la semaine dit
 * alors « Déjà inscrit »), ou écrit au parent pourquoi elle ne peut pas
 * l'accueillir. Elle place aussi l'enfant à un niveau d'accès
 * (`kid_inclusion.niveau_acces`, VALIDÉ ÉCLOSION 26/10/2026), sans toucher aux
 * besoins déclarés : l'enfant reste compté en inclusion, rapport ONE compris.
 */

/**
 * 1 : sans animateur dédié, inscription comme tout enfant ; 2 : avec un
 * animateur spécialisé, chaque inscription par une demande ; 3 : accueil
 * impossible. `null` : pas encore examiné.
 */
export type NiveauAcces = 1 | 2 | 3;

export type StatutDemande = 'pending' | 'approved' | 'rejected' | 'converted' | 'archived';

/** Une demande telle que le parent la lit (RLS : les siennes). */
export interface DemandeLue {
  id: string;
  kid_id: string;
  activity_id: string;
  status: StatutDemande;
  request_date: string;
}

/**
 * Ce que la fiche d'inclusion dit, pour décider. Le niveau arrive de la base
 * (`smallint`, 1 à 3 par contrainte) : un nombre, lu par `accesDeLEnfant`.
 */
export interface InclusionDeLEnfant {
  has_needs?: boolean | null;
  niveau_acces?: number | null;
}

/**
 * Ce que l'enfant peut faire sur la page des stages :
 *  - `libre` : s'inscrire comme tout enfant (niveau 1, ou ni niveau ni besoins déclarés) ;
 *  - `sur-demande` : chaque semaine passe par une demande (niveau 2, ou besoins
 *    déclarés pas encore examinés) ;
 *  - `impossible` : ni inscription ni demande (niveau 3).
 *
 * Un niveau décidé vaut même si le parent a décoché les besoins ensuite :
 * sinon, décocher suffirait à contourner la décision. La base applique la même
 * règle (`demander_inclusion`).
 */
export type AccesDeLEnfant = 'libre' | 'sur-demande' | 'impossible';

export function accesDeLEnfant(inclusion: InclusionDeLEnfant | null | undefined): AccesDeLEnfant {
  switch (inclusion?.niveau_acces) {
    case 3: return 'impossible';
    case 2: return 'sur-demande';
    case 1: return 'libre';
    default: return inclusion?.has_needs === true ? 'sur-demande' : 'libre';
  }
}

/**
 * Ce qu'une semaine permet à un enfant, du point de vue de l'inclusion :
 *  - `libre` : rien à demander (voir `accesDeLEnfant`, ou une demande acceptée
 *    par l'ancien circuit — `acceptee`) : le panier, comme tout le monde ;
 *  - `a-demander` : la semaine se coche pour une demande ;
 *  - `demandee` : une demande attend l'équipe, depuis `le` ;
 *  - `refusee` : la dernière demande n'a pas été retenue, `le` ;
 *  - `impossible` : l'enfant ne peut pas être accueilli (niveau 3).
 */
export type EtatInclusion =
  | { etat: 'libre'; acceptee: boolean }
  | { etat: 'a-demander' }
  | { etat: 'demandee'; le: string }
  | { etat: 'refusee'; le: string }
  | { etat: 'impossible' };

/**
 * L'état d'une semaine pour un enfant. `demandes` : celles de CET enfant pour
 * CETTE semaine (l'historique peut en porter plusieurs, doublons compris).
 *
 * Une demande acceptée l'emporte, puis une demande en attente (datée de la
 * plus ancienne : c'est elle que l'équipe a reçue la première), puis un refus.
 * « Acceptée » (`approved`) vient de l'ancien circuit, qui ouvrait la semaine au
 * panier. Une demande « convertie » a inscrit l'enfant : si l'inscription n'est
 * plus là, elle ne retient rien, pas plus qu'une demande classée.
 */
export function etatInclusion(
  inclusion: InclusionDeLEnfant | null | undefined,
  demandes: readonly DemandeLue[],
): EtatInclusion {
  const acces = accesDeLEnfant(inclusion);
  if (acces === 'impossible') return { etat: 'impossible' };
  if (acces === 'libre') return { etat: 'libre', acceptee: false };
  if (demandes.some((d) => d.status === 'approved')) return { etat: 'libre', acceptee: true };

  const enAttente = demandes.filter((d) => d.status === 'pending').map((d) => d.request_date).sort();
  if (enAttente.length > 0) return { etat: 'demandee', le: enAttente[0] };

  const refus = demandes.filter((d) => d.status === 'rejected').map((d) => d.request_date).sort();
  if (refus.length > 0) return { etat: 'refusee', le: refus[refus.length - 1] };

  return { etat: 'a-demander' };
}

/** Une case cochée de la page des stages : `semaine|enfant`. */
export const cleDeSelection = (activityId: string, kidId: string) => `${activityId}|${kidId}`;

export interface Repartition {
  /** Les cases qui vont au panier. */
  panier: string[];
  /** Les semaines à demander, par enfant. */
  demandes: Map<string, string[]>;
}

/**
 * Sépare la sélection : ce qui va au panier, ce qui part en demande. Une case
 * dont l'état ne permet ni l'un ni l'autre (demande en attente, refusée,
 * accueil impossible) n'est nulle part : la sélection survit aux changements de
 * filtres et de données, l'état se relit à l'envoi.
 */
export function repartirSelection(
  selection: readonly string[],
  etatDe: (activityId: string, kidId: string) => EtatInclusion,
): Repartition {
  const panier: string[] = [];
  const demandes = new Map<string, string[]>();
  for (const c of selection) {
    const [activityId, kidId] = c.split('|');
    const { etat } = etatDe(activityId, kidId);
    if (etat === 'libre') panier.push(c);
    else if (etat === 'a-demander') demandes.set(kidId, [...(demandes.get(kidId) ?? []), activityId]);
  }
  return { panier, demandes };
}

/** Ce que rend la fonction Edge `demander-inclusion`. */
export interface ResultatDeDemande {
  statut: 'traitee' | 'sans_besoins' | 'inscription_directe' | 'accueil_impossible';
  creees: { id: string; session_id: string }[];
  deja_demandees: string[];
  deja_inscrites: string[];
  ecartees: string[];
  courriel: 'envoye' | 'non_envoye' | 'inutile';
}

const semaines = (n: number) => `${n} semaine${n > 1 ? 's' : ''}`;

/** Le message rendu au parent après l'envoi : ce qui est parti, et ce qui l'était déjà. */
export function messageDeDemande(prenom: string, r: ResultatDeDemande): { ton: 'succes' | 'info'; texte: string } {
  if (r.statut === 'accueil_impossible') {
    return { ton: 'info', texte: `Nous ne pouvons pas accueillir ${prenom} dans nos stages pour le moment. Pour en parler : info@eclosion.be.` };
  }
  if (r.statut === 'inscription_directe') {
    return { ton: 'info', texte: `${prenom} s'inscrit sans demande : ajoutez ses semaines au panier.` };
  }
  if (r.statut === 'sans_besoins') {
    return { ton: 'info', texte: `La fiche de ${prenom} ne signale pas de besoins particuliers : ajoutez ses semaines au panier.` };
  }
  const n = r.creees.length;
  const deja = r.deja_demandees.length;
  if (n === 0) {
    return {
      ton: 'info',
      texte: deja > 0
        ? `Ces semaines sont déjà demandées pour ${prenom} : l'équipe vous répond par courriel.`
        : `Aucune nouvelle semaine à demander pour ${prenom}.`,
    };
  }
  const suite = deja > 0 ? ` (${semaines(deja)} l'étai${deja > 1 ? 'ent' : 't'} déjà)` : '';
  return {
    ton: 'succes',
    texte: `Demande envoyée pour ${prenom} : ${semaines(n)}${suite}. L'équipe vérifie l'accueil et vous répond par courriel.`,
  };
}
